// Lib
import { intersection, difference } from 'lodash/fp';

// Selectors
import { activePopupSelector } from './popupOpenSelector';

// Utils
import { isEmpty } from '../../../common/utils/immutableHelper';

// Constants
import { POPUP_OPEN, POPUP_CLOSE, POPUP_TOGGLE, POPUPS_BY_SECTION, PopupSections, PopupIds } from './popupConstants';
import { DRAG_START } from '../../reducers/draggingConstants';
import { GenericReduxThunkAction } from '../../../common/actions/actionTypes';
import {
    PopupCloseAction,
    PopupCloseMultipleAction,
    PopupOpenAction,
    PopupPredicateFn,
    PopupToggleAction,
} from './popupActionTypes';

const DEFAULT_POPUP_PREDICATE: PopupPredicateFn = (state, action) =>
    // If the next event is a DRAG_START then it's very likely that the dragged item is inside the popup
    // (as another event would come first otherwise)
    // Otherwise close the popup on any other action
    action.type === DRAG_START;

export const openPopup = ({
    popupId,
    data,
    predicateFn = DEFAULT_POPUP_PREDICATE,
}: {
    popupId: string;
    data?: Record<string, unknown>;
    predicateFn?: PopupPredicateFn;
}): PopupOpenAction => ({
    type: POPUP_OPEN,
    popupId,
    data,
    predicateFn,
});

export const closePopup = (popupId: string, closeAll = true): PopupCloseAction => ({
    type: POPUP_CLOSE,
    popupId,
    closeAll,
});

export const closeMultiplePopups = (popupIds: string[]): PopupCloseMultipleAction => ({
    type: POPUP_CLOSE,
    popupIds,
});

/**
 * Dispatches a POPUP_CLOSE action if the popup is currently open.
 */
export const closePopupIfOpen =
    (popupId: string): GenericReduxThunkAction =>
    (dispatch, getState) => {
        if (!popupId) return;

        const state = getState();
        const activePopups = activePopupSelector(state);

        // No need to do anything as there's no open popups
        if (!activePopups || !activePopups.size) return;

        if (!activePopups.has(popupId)) return;

        dispatch(closePopup(popupId));
    };

export const closeAllPopupsIfRequired = (): GenericReduxThunkAction => (dispatch, getState) => {
    const state = getState();
    const activePopups = activePopupSelector(state);

    // No need to do anything as there's no open popups
    if (!activePopups || !activePopups.size) return;

    dispatch(closePopup('', true));
};

export const closePopupsInSection =
    ({ section, excludedIds = [] }: { section: PopupSections; excludedIds: string[] }): GenericReduxThunkAction =>
    (dispatch, getState) => {
        let popupsToClose = POPUPS_BY_SECTION[section];

        if (isEmpty(popupsToClose)) return;

        const state = getState();
        const activePopupState = activePopupSelector(state);
        const activePopups = activePopupState.keySeq().toArray();

        // @ts-ignore TS struggled with the lodash/fp intersection type
        popupsToClose = difference(intersection(activePopups, popupsToClose), excludedIds) as unknown as PopupIds[];

        // No need to do anything as there's no open popups
        if (isEmpty(popupsToClose)) return;

        dispatch(closeMultiplePopups(popupsToClose));
    };

export const togglePopup = ({
    popupId,
    data,
    predicateFn = DEFAULT_POPUP_PREDICATE,
}: {
    popupId: string;
    data?: Record<string, unknown>;
    predicateFn?: PopupPredicateFn;
}): PopupToggleAction => ({
    type: POPUP_TOGGLE,
    popupId,
    data,
    predicateFn,
});

type ClosePopupPredicateFn = (activePopupId: string) => boolean;

export const closePopupsMatching =
    ({ predicateFn }: { predicateFn: ClosePopupPredicateFn }): GenericReduxThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const activePopupState = activePopupSelector(state);
        const activePopups = activePopupState.keySeq().toArray();

        const popupsToClose = activePopups.filter(predicateFn);

        // No need to do anything as there's no open popups
        if (isEmpty(popupsToClose)) return;

        dispatch(closeMultiplePopups(popupsToClose));
    };
